import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Stack } from '@mui/material';

import { PostgresFilePointer, S3FilePointer } from '@/api/data-contracts';
import { INLINE_THUMBNAIL_LIMIT } from '@/features/media/lib/constants';
import ResourceImageGallery, {
  ResourceImageGalleryItem,
} from '@/features/media/ui/ResourceImageGallery';
import { ImageResource } from '@/shared/lib/hooks/useResourceImage';

export type CellImageResource =
  | { kind: 'space-document'; spaceId: string }
  | { kind: 'space-support-picture'; spaceId: string }
  | { kind: 'service-task-document'; taskId: string }
  | { kind: 'order-document'; orderId: string }
  | { kind: 'order-artwork'; orderId: string; bookingId: string };

interface Props {
  files?: (PostgresFilePointer | S3FilePointer)[];
  resource: CellImageResource;
  onClick?: () => void;
}

const toImageResource = (resource: CellImageResource, documentId: string): ImageResource => {
  switch (resource.kind) {
    case 'space-document':
      return { kind: 'space-document', spaceId: resource.spaceId, documentId };
    case 'space-support-picture':
      return { kind: 'space-support-picture', spaceId: resource.spaceId, documentId };
    case 'service-task-document':
      return { kind: 'service-task-document', taskId: resource.taskId, documentId };
    case 'order-document':
      return { kind: 'order-document', orderId: resource.orderId, documentId };
    case 'order-artwork':
      return {
        kind: 'order-artwork',
        orderId: resource.orderId,
        bookingId: resource.bookingId,
        documentId,
      };
  }
};

const isSameResource = (a: CellImageResource, b: CellImageResource): boolean => {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'space-document':
    case 'space-support-picture':
      return a.spaceId === (b as typeof a).spaceId;
    case 'service-task-document':
      return a.taskId === (b as typeof a).taskId;
    case 'order-document':
      return a.orderId === (b as typeof a).orderId;
    case 'order-artwork':
      return a.orderId === (b as typeof a).orderId && a.bookingId === (b as typeof a).bookingId;
  }
};

const CustomCellImages: FC<Props> = ({ files, resource, onClick }) => {
  const { t } = useTranslation();

  const items = useMemo<ResourceImageGalleryItem[]>(
    () =>
      (files ?? [])
        .filter((file) => file.objectId != null)
        .map((file) => ({
          resource: toImageResource(resource, String(file.objectId)),
          name: file.name,
        })),
    [files, resource],
  );

  if (!items.length) {
    return (
      <Stack direction="row" alignItems="center">
        <p>{t('not_uploaded')}</p>
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center">
      <ResourceImageGallery
        items={items}
        max={INLINE_THUMBNAIL_LIMIT}
        onThumbnailClick={onClick ? () => onClick() : undefined}
      />
    </Stack>
  );
};

export default memo(CustomCellImages, (prevProps, nextProps) => {
  const filesEqual =
    prevProps.files === nextProps.files ||
    (prevProps.files?.length === nextProps.files?.length &&
      prevProps.files?.every((file, index) => file === nextProps.files?.[index])) ||
    false;

  return (
    filesEqual &&
    isSameResource(prevProps.resource, nextProps.resource) &&
    prevProps.onClick === nextProps.onClick
  );
});
